import { useContext } from "react";
import PageContext from "src/components/Context/PageContext";
import { COUNTRIES, useCountry } from "src/components/Context/CountryContext";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";

const defaultOnlineLocationByCountry: { [key in COUNTRIES]: number } = {
  [COUNTRIES.NZ]: 30,
  [COUNTRIES.AU]: 115,
};

const defaultSupportOfficeLocationByCountry: { [key in COUNTRIES]: number } = {
  [COUNTRIES.NZ]: 10,
  [COUNTRIES.AU]: 1,
};

type CurrentUserStoreResult = {
  onlineLocationId: number;
  currentLocationId: number;
  inventoryLocationId: number;
  isSupportOffice: boolean;
};

export const useCurrentUserStore = (): CurrentUserStoreResult => {
  const { country } = useCountry();

  const [contextData] = useContext(PageContext);
  const { userProfile, physicalUserStoreLocationId } = contextData;

  const supportOfficeLocation = defaultSupportOfficeLocationByCountry[country];
  const hasCrossCountryAccess =
    hasPermission(UserRoles.AccessAU, userProfile.roles) && hasPermission(UserRoles.AccessNZ, userProfile.roles);
  let currentStoreId = physicalUserStoreLocationId || userProfile?.storeNumber;
  let overrideStoreId = null;

  // For support office users accessing a different country, override to the support office location of that country they are accessing
  if (
    hasCrossCountryAccess &&
    currentStoreId === defaultSupportOfficeLocationByCountry[COUNTRIES.AU] &&
    country === COUNTRIES.NZ
  ) {
    overrideStoreId = defaultSupportOfficeLocationByCountry[COUNTRIES.NZ];
  } else if (
    hasCrossCountryAccess &&
    currentStoreId === defaultSupportOfficeLocationByCountry[COUNTRIES.NZ] &&
    country === COUNTRIES.AU
  ) {
    overrideStoreId = defaultSupportOfficeLocationByCountry[COUNTRIES.AU];
  }

  currentStoreId = overrideStoreId ?? currentStoreId;

  return {
    onlineLocationId: defaultOnlineLocationByCountry[country],
    currentLocationId: currentStoreId || supportOfficeLocation,
    inventoryLocationId: currentStoreId || supportOfficeLocation,
    isSupportOffice: currentStoreId ? currentStoreId === supportOfficeLocation : true,
  };
};
